import { useEffect } from "react";

import { colocaToast } from "@/shared/utils/toast";

import { type AlertBurst, joinBurst } from "./dashboardAlertBursts";
import { DASHBOARD_ALERT_DURATION_MS } from "./dashboardAlertBursts";
import { type DashboardAlertType, soundForAlert } from "./dashboardAlertCatalog";
import {
  announcementIdFor,
  burstSummaryAlert,
  type DashboardAlert,
  type DashboardAlertTextContext,
} from "./dashboardAlertEvents";
import { noteDashboardAlertShown } from "./dashboardAlertIntro";
import {
  dashboardAlertModeOf,
  dashboardAlertPlaysSound,
  readDashboardAlertPreferences,
  useDashboardAlertPreferences,
} from "./dashboardAlertPreferences";
import { playDashboardAlertSound, unlockDashboardAlertSound } from "./dashboardAlertSounds";

export interface DashboardAlertPresenterContext extends DashboardAlertTextContext {
  navigate: (href: string) => void;
}

const TOAST_BY_VARIANT = {
  success: colocaToast.success,
  error: colocaToast.error,
  warning: colocaToast.warning,
  default: colocaToast.default,
} as const;

/**
 * Shows alerts as toasts, honoring each alert's mode (with sound, silent, off) and the master sound switch,
 * playing the sound of the alert's section, and folding a burst of one kind into a single summary that keeps
 * counting (see `dashboardAlertBursts`), which rings when it appears and then counts in silence. One
 * presenter per mounted listener: the bursts it tracks are its own.
 */
export function createDashboardAlertPresenter(getContext: () => DashboardAlertPresenterContext) {
  const bursts = new Map<DashboardAlertType, AlertBurst>();

  return function present(alert: DashboardAlert): void {
    const prefs = readDashboardAlertPreferences();
    if (dashboardAlertModeOf(prefs, alert.type) === "off") return;
    const context = getContext();
    const toastId = `dashboard-alert:${announcementIdFor(alert.type, alert.id)}`;
    const step = joinBurst(
      bursts.get(alert.type),
      { type: alert.type, toastId, amount: alert.amount, currency: alert.currency },
      Date.now(),
    );
    bursts.set(alert.type, step.burst);
    step.dismiss.forEach((id) => colocaToast.dismiss(id));
    const shown = step.mode === "single" ? alert : burstSummaryAlert(step.burst, alert.variant, context);
    TOAST_BY_VARIANT[shown.variant](shown.title, {
      id: step.mode === "single" ? toastId : `dashboard-alert-summary:${alert.type}`,
      description: shown.description ?? undefined,
      duration: DASHBOARD_ALERT_DURATION_MS,
      action: { label: shown.action.label, onClick: () => context.navigate(shown.action.href) },
    });
    if (step.rings && dashboardAlertPlaysSound(prefs, alert.type)) {
      playDashboardAlertSound(
        soundForAlert(alert.type, alert.variant === "error" || alert.variant === "warning"),
      );
    }
    noteDashboardAlertShown();
  };
}

/**
 * Browsers only let a page make sound after it was touched (Safari only wakes audio inside the gesture
 * itself), so while the sound is on, the next tap or key on the page wakes the audio output for the alert
 * that comes later.
 */
export function useUnlockDashboardAlertSound(active = true) {
  const { sound } = useDashboardAlertPreferences();
  useEffect(() => {
    if (!active || !sound) return;
    const unlock = () => unlockDashboardAlertSound();
    document.addEventListener("pointerdown", unlock, { once: true });
    document.addEventListener("keydown", unlock, { once: true });
    return () => {
      document.removeEventListener("pointerdown", unlock);
      document.removeEventListener("keydown", unlock);
    };
  }, [active, sound]);
}
